import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@mui/material';
import { Grid } from '@mui/system';
import { isEmpty } from 'lodash';
import { enqueueSnackbar } from 'notistack';
import { useParams } from 'react-router-dom';
import { FundsService } from 'api';
import { captableAction } from 'common/actions';
import { CASH_FLOW_LEDGER_LOCKED_MESSAGE, CASH_FLOW_LEDGER_SLUG } from 'common/constants/cap-table';
import { useStore } from 'common/store';
import { UseStoreValues } from 'common/types/store';
import { Alert, AllocationFinalMessage, CustomContainer } from 'components';
import { FinalAllocation } from 'components/AllocationFinalMessage/AllocationFinalMessage.props';
import { GridSkeleton } from 'components/Grid';
import ScalarSpreadsheet from 'components/ScalarSpreadsheet';
import useWorkbook from 'components/ScalarSpreadsheet/utilities/useWorkbook';
import WorkbookContext from 'components/ScalarSpreadsheet/utilities/WorkbookContext';
import { useTertiaryNavigationRegistration } from 'context/FirmBaseLayoutContext';
import { useLayoutContext } from 'context/LayoutContext';
import { useExportSignal } from 'features/pdfExport';
import { CashFlowsProps } from 'pages/CapTable/cash-distributions/CashFlows.props';
import { useAllSecurityOptions } from 'pages/CapTable/cash-distributions/hooks/useAllSecurityOptions';
import { useEntityOptions } from 'pages/CapTable/cash-distributions/hooks/useEntityOptions';
import { useOwnershipOptions } from 'pages/CapTable/cash-distributions/hooks/useOwnershipOptions';
import useCapTableTertiaryNavItems from 'pages/CapTable/useCapTableTertiaryNavItems';
import { PdfExportTargets } from 'pages/Valuations/util/constants';
import { useTableValidation } from 'services/hooks';
import { useCaptableCashFlowLedger } from 'services/hooks/captable';
import { Logger } from 'utilities';
import { CashFlowSummary } from './components/CashFlowSummary';
import CashFlowContext from './context/CashFlowContext';
import { CashFlowColumnAlias } from './data/constants';
import { CashFlowLedgerItem, CashFlowSourceKind } from './data/types';
import { useCashDistributionsSpreadsheetConfig } from './hooks/useCashFlowLedgerSpreadsheetConfig';
// `useGrossIRRSummary` (client-side xirr) is intentionally kept on disk but
// no longer wired to the Cash Flow Summary table. Server-side
// `useServerGrossIRRSummary` calls the captable gross-irrs endpoint so the
// CFL summary IRR is byte-identical to waterfall / fund summary / firm
// summary IRRs (single source of truth in `cap_table.services.gross_irr`).
import { useServerGrossIRRSummary } from './hooks/useServerGrossIRRSummary';

const CashFlows = ({
  capTableVersionsByCompany,
  selectedMeasurementDate,
  isActiveTab = true,
  saveAsNewProforma,
}: CashFlowsProps) => {
  const { firmSlugParam, companySlugParam } = useParams<{ firmSlugParam: string; companySlugParam: string }>();
  const capTableNavItems = useCapTableTertiaryNavItems(firmSlugParam ?? '', companySlugParam ?? '');

  const { fetchCashFlowLedger, updateCashFlowLedger } = useCaptableCashFlowLedger();
  const {
    updatePageActions,
    togglePdfDialog,
    toggleMainForm,
    viewOnlyUser,
    isExportMode,
  } = useLayoutContext();
  const [{ captableInfo, fundList, companyInfo, firmInfo }] = useStore() as unknown as UseStoreValues;
  const [cashFlows, setCashFlows] = useState<CashFlowLedgerItem[]>([]);
  const [hasFetchedLedger, setHasFetchedLedger] = useState(false);
  const [isAllocationFinal, setIsAllocationFinal] = useState<boolean>(false);
  const [isDisabled, setIsDisabled] = useState<boolean>(false);
  const [finalAllocations, setFinalAllocations] = useState<FinalAllocation[]>([]);
  // Keep a ref to cashFlows for use in callbacks
  const cashFlowsRef = useRef<CashFlowLedgerItem[]>([]);
  useEffect(() => {
    cashFlowsRef.current = cashFlows;
  }, [cashFlows]);

  const sortCashFlowsByDate = useCallback(
    (flows: CashFlowLedgerItem[]) =>
      [...flows].sort((a, b) => {
        const dateA = a.date_of_transaction ? new Date(a.date_of_transaction).getTime() : 0;
        const dateB = b.date_of_transaction ? new Date(b.date_of_transaction).getTime() : 0;
        return dateA - dateB;
      }),
    []
  );

  // Merge current spreadsheet data with persisted data
  const mergeSpreadsheetDataWithPersisted = useCallback((
    spreadsheetData: CashFlowLedgerItem[],
    persistedFlows = cashFlowsRef.current
  ) => {

    // Create a map of reversed data by ID (for items that have IDs)
    // Using string keys to avoid number/string type mismatch during comparison
    const reversedById = new Map<string, CashFlowLedgerItem>();
    const newItems: CashFlowLedgerItem[] = [];

    spreadsheetData.forEach(item => {
      if (item.id) {
        reversedById.set(String(item.id), item);
      } else {
        newItems.push(item);
      }
    });

    // Only keep persisted items that are saved (id) or virtual.
    const persistedItems = persistedFlows.filter(item => item.id || item.is_virtual);

    // We use a shallow merge here to preserve rich fields from the database with
    // the live spreadsheet state (like unsaved exclusion toggles or edited amounts).
    const mergedItems = persistedItems.map(persistedItem => {
      const stringId = persistedItem.id ? String(persistedItem.id) : null;
      if (stringId && reversedById.has(stringId)) {
        return { ...persistedItem, ...reversedById.get(stringId) };
      }
      // Keep the original persisted item
      return persistedItem;
    });

    // Add any new items (without IDs) from the spreadsheet
    return [...mergedItems, ...newItems];
  }, []);

  const { securityOptionsByEntity, loading: securityOptionsLoading } = useAllSecurityOptions({
    companyInfo,
    captableInfo,
    capTableVersionsByCompany,
  });

  // Placeholder ref for spreadsheet - will be populated after config is created
  const spreadsheetRef = useRef<typeof spreadsheet>(null);

  const handleDeleteRow = useCallback(
    (rowIndex: number) => {
      const currentSpreadsheet = spreadsheetRef.current;
      if (!currentSpreadsheet) {
        return;
      }

      // Get current spreadsheet data including unsaved changes
      const reversedData: CashFlowLedgerItem[] = currentSpreadsheet.reverseParse();

      // Merge with persisted data to avoid losing database rows
      const mergedData = mergeSpreadsheetDataWithPersisted(reversedData);

      // Remove the row at the specified index (rowIndex is 1-based, so subtract 1)
      const updatedFlows = [...mergedData];
      updatedFlows.splice(rowIndex - 1, 1);
      setCashFlows(updatedFlows);
    },
    [mergeSpreadsheetDataWithPersisted]
  );

  // Live FX preview writeback. Routes through the workbook's `onChange`
  // (rather than `setCells`) because the spreadsheet renders from
  // `sheet.cells` — the SpreadsheetConfig's INTERNAL cell map — not from
  // useWorkbook's React state. `onChange` runs the full update path
  // (`sheet.afterCellChanged` -> `cell.onChange` -> validation), so the
  // converted amount lands in both the framework's internal state and
  // the rendered DOM. Going through `setCells` alone would update the
  // outer state but leaves the sheet's internal cells stale, so the cell
  // would never actually display the new value.
  //
  // Cells + onChange come from `useWorkbook`, which runs AFTER this hook
  // chain (it needs `spreadsheets` which needs this callback). Forward-
  // declare refs that get populated by the effect blocks below `useWorkbook`,
  // so the callback's identity stays stable across renders and the parser
  // wrapper in `useCashDistributionsSpreadsheetConfig` doesn't churn.
  const cellsRef = useRef<any>(undefined);
  const onChangeRef = useRef<((cell: any, value: any) => void) | null>(null);

  const handlePreFxHostPreview = useCallback((cellKey: string, value: number | null) => {
    const sheetCells = cellsRef.current?.CashDistributions;
    const targetCell = sheetCells?.[cellKey];
    if (!targetCell || !onChangeRef.current) {
      return;
    }
    onChangeRef.current(targetCell, value);
  }, []);

  const { spreadsheet } = useCashDistributionsSpreadsheetConfig({
    ledgerData: cashFlows,
    isDisabled,
    securityOptionsByEntity,
    onDeleteRow: handleDeleteRow,
    onPreFxHostPreview: handlePreFxHostPreview,
  });

  // Update spreadsheet ref when spreadsheet changes
  useEffect(() => {
    spreadsheetRef.current = spreadsheet;
  }, [spreadsheet]);

  const ownershipOptions = useOwnershipOptions({ companyInfo, fundList, cashFlows });
  const entityOptions = useEntityOptions({ companyInfo });

  const cashFlowContextValue = useMemo(
    () => ({
      ownershipOptions,
      entityOptions,
      securityOptionsByEntity,
      securityOptionsLoading,
    }),
    [ownershipOptions, entityOptions, securityOptionsByEntity, securityOptionsLoading]
  );

  const spreadsheets = useMemo(() => (spreadsheet ? [spreadsheet] : []), [spreadsheet]);
  const { onChange, cells, workbook, workbookContextValue, data, setCells } = useWorkbook(spreadsheets);
  const { validateCells } = useTableValidation();

  // Populate the refs forward-declared above so `handlePreFxHostPreview`
  // can reach `onChange` / `cells` without making them React deps that
  // would invalidate the parser's wrapped callback on every render.
  useEffect(() => {
    cellsRef.current = cells;
  }, [cells]);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  // Virtual rows (FO-derived) cannot round-trip through CFL bulk save - the
  // reverse parser skips them. Toggling exclusion on a virtual Amount cell
  // routes to the matching FO source PATCH endpoint, then refetches so the
  // refreshed snapshot picks up the new flag (and the IRR summary updates).
  // UNREALIZED_VALUE rows are not togglable; the column viewer skips them.
  const captableId = captableInfo?.id;
  const persistVirtualExclusion = useCallback(
    async (sourceKind: CashFlowSourceKind | undefined, sourceId: number | undefined, isExcluded: boolean) => {
      if (!sourceId || !sourceKind) return;
      const payload = { is_excluded_from_calculations: isExcluded };
      try {
        if (sourceKind === 'shares_acquisition') {
          await FundsService.apiFundsSharesAcquisitionExclusionPartialUpdate(sourceId, payload);
        } else if (sourceKind === 'shares_sale') {
          await FundsService.apiFundsSharesSaleExclusionPartialUpdate(sourceId, payload);
        } else if (sourceKind === 'cash_distribution') {
          await FundsService.apiFundsCashDistributionExclusionPartialUpdate(sourceId, payload);
        } else {
          return; // unrealized_value: not togglable
        }
        if (captableId) {
          const flows = await fetchCashFlowLedger(captableId);
          const freshFlows = (flows || []) as unknown as CashFlowLedgerItem[];
          const currentSpreadsheet = spreadsheetRef.current;
          if (currentSpreadsheet) {
            const reversedData = currentSpreadsheet.reverseParse() as CashFlowLedgerItem[];
            setCashFlows(sortCashFlowsByDate(mergeSpreadsheetDataWithPersisted(reversedData, freshFlows)));
          } else {
            setCashFlows(sortCashFlowsByDate(freshFlows));
          }
        }
      } catch (error) {
        Logger.error('Failed to persist CFL exclusion toggle on virtual row', error);
        enqueueSnackbar(
          'Could not update inclusion of cash flow row. Please retry or refresh.',
          { variant: 'error' }
        );
      }
    },
    [captableId, fetchCashFlowLedger, mergeSpreadsheetDataWithPersisted, sortCashFlowsByDate]
  );

  // Wraps useWorkbook's onChange. Real-row toggles flow through the existing
  // bulk-save-on-Save flow (reverse parser carries `is_excluded_from_calculations`).
  // Virtual-row Amount toggles fire a single FO source PATCH immediately.
  const handleChange = useCallback(
    (cell: any, value: any, customFn?: any) => {
      if (
        cell?.is_virtual
        && cell?.alias === CashFlowColumnAlias.AMOUNT
        && cell?.source_kind
        && cell?.source_id !== undefined
      ) {
        persistVirtualExclusion(cell.source_kind, cell.source_id, !cell.enabled);
      }
      return onChange(cell, value, customFn);
    },
    [onChange, persistVirtualExclusion]
  );

  // Note: We intentionally do NOT sync spreadsheet data to cashFlows on every edit
  // because that causes the spreadsheet config to be recreated, causing re-renders.
  // The IRR calculation will use slightly stale data until the next save/refresh,
  // but this is acceptable for better UX.

  // Fetch gross IRR data for the summary table (must be after ownershipOptions is created)
  const {
    fundsGrossIrr,
    firmGrossIrr,
    fundsInvestments,
    fundsProceeds,
    firmInvestments,
    firmProceeds,
    fundIdToName,
  } = useServerGrossIRRSummary({
    captableId,
    fundList,
    cashFlows,
    ownershipOptions,
    // Trigger refetch when the underlying cash flows change so the IRR
    // updates after a virtual-row exclusion toggle or a bulk save.
    refreshKey: cashFlows.length,
  });

  // Refetch cash flow ledger when the tab becomes active to pick up changes from Fund Ownership
  useEffect(() => {
    if (isActiveTab && captableInfo?.id) {
      fetchCashFlowLedger(captableInfo.id).then(flows => {
        const freshFlows = (flows || []) as unknown as CashFlowLedgerItem[];
        const currentSpreadsheet = spreadsheetRef.current;
        if (currentSpreadsheet) {
          const reversedData = currentSpreadsheet.reverseParse() as CashFlowLedgerItem[];
          setCashFlows(sortCashFlowsByDate(mergeSpreadsheetDataWithPersisted(reversedData, freshFlows)));
        } else {
          setCashFlows(sortCashFlowsByDate(freshFlows));
        }
        setHasFetchedLedger(true);
      });
    }
  }, [captableInfo?.id, fetchCashFlowLedger, mergeSpreadsheetDataWithPersisted, sortCashFlowsByDate, isActiveTab]);

  let cashFlowExportStatus: 'loading' | 'missing' | 'ready' = 'ready';
  if (!spreadsheet || !hasFetchedLedger) {
    cashFlowExportStatus = 'loading';
  } else if (isEmpty(cashFlows)) {
    cashFlowExportStatus = 'missing';
  }
  useExportSignal({
    target: PdfExportTargets.CASH_FLOW_LEDGER.id,
    status: cashFlowExportStatus,
    enabled: isExportMode,
  });

  // Check allocation finalization status on mount
  useEffect(() => {
    if (captableInfo?.id) {
      const verifyAllocationStatus = async () => {
        try {
          const allocations = (await captableAction.verifyIfHasFinalAllocation(captableInfo.id!)) as FinalAllocation[];

          setIsAllocationFinal(!isEmpty(allocations));
          setIsDisabled(!isEmpty(allocations) || viewOnlyUser);
          setFinalAllocations(allocations);

          if (isExportMode) {
            setIsAllocationFinal(true);
            setIsDisabled(true);
          }
        } catch (error) {
          Logger.error('Error verifying allocation status:', error);
        }
      };
      verifyAllocationStatus();
    }
  }, [captableInfo?.id, viewOnlyUser, isExportMode]);

  // Callback for duplicate/save as proforma
  const savesAsProformaConfirmation = useCallback(() => {
    saveAsNewProforma?.({});
  }, [saveAsNewProforma]);

  const handleAddRow = useCallback(() => {
    const currentSpreadsheet = spreadsheetRef.current;
    if (!currentSpreadsheet) {
      return;
    }

    // Get current spreadsheet data including unsaved changes
    const reversedData: CashFlowLedgerItem[] = currentSpreadsheet.reverseParse();

    // Merge with persisted data to avoid losing database rows
    const mergedData = mergeSpreadsheetDataWithPersisted(reversedData);

    const newItem: CashFlowLedgerItem = {
      date_of_transaction: '',
      amount: 0,
      transaction_type: '',
      is_virtual: false,
      is_excluded_from_calculations: false,
    };

    // Add new row to the merged data
    setCashFlows([...mergedData, newItem]);
  }, [mergeSpreadsheetDataWithPersisted]);

  const handleAddMultipleRows = useCallback(
    (numRows = 1) => {
      const currentSpreadsheet = spreadsheetRef.current;
      if (!currentSpreadsheet) {
        return;
      }

      // Get current spreadsheet data including unsaved changes
      const reversedData: CashFlowLedgerItem[] = currentSpreadsheet.reverseParse();

      // Merge with persisted data to avoid losing database rows
      const mergedData = mergeSpreadsheetDataWithPersisted(reversedData);

      // Create multiple new items
      const newItems: CashFlowLedgerItem[] = Array.from({ length: numRows }, () => ({
        date_of_transaction: '',
        amount: 0,
        transaction_type: '',
        is_virtual: false,
        is_excluded_from_calculations: false,
      }));

      // Add all new rows to the merged data
      setCashFlows([...mergedData, ...newItems]);
    },
    [mergeSpreadsheetDataWithPersisted]
  );

  const handleSave = useCallback(async () => {
    const currentSpreadsheet = spreadsheetRef.current;
    if (!currentSpreadsheet || !captableInfo?.id) {
      return;
    }

    const sheetName = currentSpreadsheet.name;
    const sheetCells = sheetName ? cells?.[sheetName] : undefined;
    if (!sheetCells) {
      return;
    }

    const cellsToValidate = Object.values(sheetCells).filter(
      cell => !cell.readOnly || (cell as any).validateWhenReadOnly
    );
    const { areCellsValid: areSheetCellsValid } = validateCells({
      cellsToValidate,
      customValidations: currentSpreadsheet.customValidations,
      parsedColumns: currentSpreadsheet.cells,
      tableData: currentSpreadsheet.tableData,
      isSaving: true,
      useOriginalCell: true,
    });

    if (sheetName) {
      setCells(prev => ({
        ...prev,
        [sheetName]: { ...sheetCells },
      }));
    }

    if (!areSheetCellsValid) {
      return Promise.reject();
    }

    const reversedData: CashFlowLedgerItem[] = currentSpreadsheet.reverseParse();
    try {
      await updateCashFlowLedger(captableInfo.id, reversedData);
      // Refresh cash flows after save to get updated IDs and resort
      const flows = await fetchCashFlowLedger(captableInfo.id);
      setCashFlows(sortCashFlowsByDate(flows || []));
    } catch {
      // Error is logged in the hook
      return Promise.reject();
    }
  }, [
    captableInfo?.id,
    cells,
    fetchCashFlowLedger,
    setCells,
    sortCashFlowsByDate,
    updateCashFlowLedger,
    validateCells,
  ]);

  // Keep a ref to handleSave to provide a stable callback to updatePageActions
  const handleSaveRef = useRef(handleSave);
  useEffect(() => {
    handleSaveRef.current = handleSave;
  }, [handleSave]);
  const stableHandleSave = useCallback(() => handleSaveRef.current(), []);

  const tertiaryNavState = useMemo(() => {
    if (isEmpty(captableInfo)) {
      return null;
    }
    return {
      ariaLabel: 'Cash Flow Ledger',
      items: capTableNavItems,
      actions: isAllocationFinal !== undefined ? [
        {
          type: 'splitButton' as const,
          label: 'Save',
          variant: 'positive' as const,
          disabled: isDisabled,
          locked: isAllocationFinal,
          onClick: stableHandleSave,
        },
      ] : [],
    };
  }, [captableInfo, capTableNavItems, isDisabled, isAllocationFinal, stableHandleSave]);
  useTertiaryNavigationRegistration(tertiaryNavState);

  // Register the save action only while the cash flow ledger tab owns the active page actions.
  useEffect(() => {
    if (isActiveTab && !isEmpty(captableInfo)) {
      updatePageActions?.({
        mainAction: {
          id: CASH_FLOW_LEDGER_SLUG,
          label: 'Save',
          isActive: isDisabled,
          isLock: isAllocationFinal,
          callback: () => handleSaveRef.current(),
        },
      });
    }
    return () => {
      if (isActiveTab) {
        updatePageActions?.(null);
      }
    };
  }, [
    toggleMainForm,
    togglePdfDialog,
    updatePageActions,
    captableInfo,
    companyInfo,
    isActiveTab,
    isDisabled,
    isAllocationFinal,
  ]);

  if (!spreadsheet) {
    return (
      <CustomContainer>
        <GridSkeleton />
      </CustomContainer>
    );
  }

  return (
    <CustomContainer>
      <Grid container spacing={2}>
        {/* Locked state banner with Unlock/Duplicate */}
        {isDisabled && isAllocationFinal && !isExportMode && (
          <Grid size={{ xs: 12 }}>
            <AllocationFinalMessage
              tableTerms={{ tableName: 'Cash Flow Ledger' }}
              finalAllocations={finalAllocations}
              setIsDisabled={setIsDisabled}
              savesAsProformaConfirmation={savesAsProformaConfirmation}
              variant
              actions={[
                {
                  label: 'Duplicate',
                  buttonProps: {
                    variant: 'contained',
                    size: 'small',
                    disabled: viewOnlyUser,
                  },
                  callback: savesAsProformaConfirmation,
                },
              ]}
              viewOnlyUser={viewOnlyUser}
            />
          </Grid>
        )}

        {/* Alert when unlocked but still final */}
        {isAllocationFinal && !isDisabled && !isExportMode && (
          <Grid size={{ xs: 12 }}>
            <Alert isAlertVisible>{CASH_FLOW_LEDGER_LOCKED_MESSAGE}</Alert>
          </Grid>
        )}

        <Grid size={{ xs: 12 }} data-testid="cfl-spreadsheet">
          <CashFlowContext.Provider value={cashFlowContextValue}>
            <WorkbookContext.Provider value={workbookContextValue}>
              {cells && data && (
                <ScalarSpreadsheet
                  {...spreadsheet}
                  cells={cells}
                  showTitlesColumn={false}
                  showToolbar={false}
                  onChange={handleChange}
                  workbook={workbook}
                  sheet={spreadsheet}
                  data={data}
                  displayDeleteRowBtn={!isDisabled}
                  deleteRowFn={handleDeleteRow}
                  addMultipleRows={handleAddMultipleRows}
                />
              )}
            </WorkbookContext.Provider>
          </CashFlowContext.Provider>
        </Grid>
        {!isExportMode && (
          <Grid size={{ xs: 12 }} sx={{ pl: '12px' }}>
            <Button
              onClick={handleAddRow}
              variant="contained"
              disabled={isDisabled}
              data-testid="cfl-add-transaction">
              Add Transaction
            </Button>
          </Grid>
        )}
        <Grid size={{ xs: 12 }}>
          <CashFlowSummary
            fundsGrossIrr={fundsGrossIrr}
            firmGrossIrr={firmGrossIrr}
            fundsInvestments={fundsInvestments}
            fundsProceeds={fundsProceeds}
            firmInvestments={firmInvestments}
            firmProceeds={firmProceeds}
            fundIdToName={fundIdToName}
            firmName={companyInfo?.name || firmInfo?.name || 'Total'}
          />
        </Grid>
      </Grid>
    </CustomContainer>
  );
};

export default CashFlows;
